import NetworkManager from './NetworkManager';
import type { AuditAction, AuditEntityType, AuditLog } from '@byte-store-control/shared';

// Re-export types for convenience
export type { AuditAction, AuditEntityType, AuditLog };

/**
 * Query options for fetching audit logs
 */
export interface AuditQueryOptions {
  limit?: number;
  startDate?: string;
  endDate?: string;
  startKey?: string;
}

/**
 * Response from audit log queries
 */
export interface AuditResponse {
  logs: AuditLog[];
  count: number;
  nextKey: string | null;
  /** True when the backend skipped one or more stores due to per-store query timeouts. */
  partial?: boolean;
  /** Number of stores whose queries were skipped due to timeout/error. */
  failedStoreCount?: number;
}

/**
 * Build query string from options
 */
function buildQueryParams(options: AuditQueryOptions): string {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.startDate) params.append('startDate', options.startDate);
  if (options.endDate) params.append('endDate', options.endDate);
  if (options.startKey) params.append('startKey', options.startKey);
  return params.toString();
}

/**
 * Get audit logs for a specific store
 */
export async function getStoreAuditLogs(
  storeNumber: string,
  options: AuditQueryOptions = {}
): Promise<AuditResponse> {
  const queryString = buildQueryParams(options);
  const url = `/stores/${storeNumber}/audit${queryString ? `?${queryString}` : ''}`;
  const response = await NetworkManager.get<{ status: boolean; data: AuditResponse }>(url);
  return response.data || { logs: [], count: 0, nextKey: null };
}

/**
 * Get current user's activity history
 */
export async function getMyActivity(options: AuditQueryOptions = {}): Promise<AuditResponse> {
  const queryString = buildQueryParams(options);
  const url = `/audit/my-activity${queryString ? `?${queryString}` : ''}`;
  const response = await NetworkManager.get<{ status: boolean; data: AuditResponse }>(url);
  return response.data || { logs: [], count: 0, nextKey: null };
}

/**
 * Get audit logs by entity type
 */
export async function getAuditLogsByType(
  entityType: AuditEntityType,
  options: AuditQueryOptions = {}
): Promise<AuditResponse> {
  const queryString = buildQueryParams(options);
  const url = `/audit/by-type/${entityType}${queryString ? `?${queryString}` : ''}`;
  const response = await NetworkManager.get<{ status: boolean; data: AuditResponse }>(url);
  return response.data || { logs: [], count: 0, nextKey: null };
}

/**
 * Get audit logs for all accessible stores
 * This aggregates logs from multiple stores the user has access to
 */
export async function getAllAuditLogs(options: AuditQueryOptions = {}): Promise<AuditResponse> {
  const queryString = buildQueryParams(options);
  const url = `/audit/all${queryString ? `?${queryString}` : ''}`;
  const response = await NetworkManager.get<{ status: boolean; data: AuditResponse }>(url);
  return response.data || { logs: [], count: 0, nextKey: null };
}

/**
 * Last modified info for a store
 */
export interface StoreLastModified {
  timestamp: string;
  action: string;
  userName: string;
}

/**
 * Get last modified info for multiple stores
 * Efficient batch query - one LIMIT 1 query per store using primary key
 */
export async function getStoresLastModified(
  storeNumbers: string[]
): Promise<Record<string, StoreLastModified>> {
  if (storeNumbers.length === 0) {
    return {};
  }

  const response = await NetworkManager.post<{
    status: boolean;
    data: Record<string, StoreLastModified>;
  }>('/stores/last-modified', { storeNumbers });

  return response.data || {};
}
